import { useState, useEffect } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  Toolbar,
  IconButton,
  Tooltip,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CategoryIcon from '@mui/icons-material/Category';
import PeopleIcon from '@mui/icons-material/People';
import WorkIcon from '@mui/icons-material/Work';
import BusinessIcon from '@mui/icons-material/Business';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SearchIcon from '@mui/icons-material/Search';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventNoteIcon from '@mui/icons-material/EventNote';
import MapIcon from '@mui/icons-material/Map';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import BuildIcon from '@mui/icons-material/Build';
import BarChartIcon from '@mui/icons-material/BarChart';
import { apiRequest } from '../utils/api';
import type { User } from '../types';
import glasskaLogoBlue from '../assets/Glasska_logo_blue.png';
import logo from '../assets/logo.png';

interface SidebarProps {
  drawerWidth: number;
  minimized: boolean;
  onToggleMinimize: () => void;
  mobileOpen: boolean;
  handleDrawerToggle: () => void;
  onMenuClick?: (menuItem: string) => void;
  currentPage?: string;
}

export default function Sidebar({
  drawerWidth,
  minimized,
  onToggleMinimize,
  mobileOpen,
  handleDrawerToggle,
  onMenuClick,
  currentPage = 'customers',
}: SidebarProps) {
  const [reportsOpen, setReportsOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  // Auto-expand Reports menu when Sales or Traffic is selected
  useEffect(() => {
    if (currentPage === 'sales' || currentPage === 'traffic') {
      setReportsOpen(true);
    }
  }, [currentPage]);

  // Close Reports menu when sidebar is minimized
  useEffect(() => {
    if (minimized && reportsOpen) {
      setReportsOpen(false);
    }
  }, [minimized]);


  // Load current user profile to check role
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const response = await apiRequest('/users/profile/me', { method: 'GET' });
        setCurrentUser(response.data as User);
      } catch (error) {
        console.error('Failed to load current user:', error);
      }
    };
    loadCurrentUser();
  }, []);

  // Check if user is a Field Worker
  const isFieldWorker = currentUser?.role === 'FIELD AND SHOP TEAM';
  const isAdmin = currentUser?.role === 'SYSTEM ADMIN';

  const handleListItemClick = (menuItem: string) => {
    if (onMenuClick) {
      onMenuClick(menuItem);
    }
    // Auto-expand Reports when clicking Sales or Traffic
    if (menuItem === 'sales' || menuItem === 'traffic') {
      setReportsOpen(true);
    }
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ justifyContent: minimized ? 'center' : 'space-between', minHeight: '64px !important', position: 'relative' }}>
        {!minimized && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              flex: '1 1 auto',
              maxWidth: 'calc(100% - 48px)',
            }}
          >
            <img
              src={glasskaLogoBlue}
              alt="Glasska Logo"
              style={{
                height: '40px',
                width: 'auto',
                objectFit: 'contain',
                marginLeft: 'auto'
              }}
            />
          </Box>
        )}
        <IconButton
          onClick={onToggleMinimize}
          sx={{ 
            ml: minimized ? 0 : 1,
            position: 'relative',
            zIndex: 1,
            display: { xs: 'none', md: 'flex' }
          }}
          size="small"
        >
          {minimized ? (
            <img
              src={logo}
              alt="Toggle Menu"
              style={{
                height: '48px',
                width: 'auto',
                objectFit: 'contain',
              }}
            />
          ) : (
            <ChevronLeftIcon />
          )}
        </IconButton>
      </Toolbar>
      <Divider />
      {!isFieldWorker && (
        <>
          <List>
            {!minimized && (
              <Typography variant="overline" sx={{ px: 2, py: 1, display: 'block' }}>
                Main items
              </Typography>
            )}
        <ListItem disablePadding>
          <Tooltip title={minimized ? "Dashboard" : ""} placement="right">
            <ListItemButton
              selected={currentPage === 'materialstatusdashboard'}
              onClick={() => handleListItemClick('materialstatusdashboard')}
              sx={{
                backgroundColor:
                  currentPage === 'materialstatusdashboard' ? 'action.selected' : 'transparent',
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  },
                },
                justifyContent: minimized ? 'center' : 'flex-start',
                minHeight: 48,
                px: minimized ? 1 : 2,
              }}
            >
              <ListItemIcon sx={{ minWidth: minimized ? 0 : 56, justifyContent: 'center' }}>
                <DashboardIcon />
              </ListItemIcon>
              {!minimized && <ListItemText primary="Dashboard" />}
            </ListItemButton>
          </Tooltip>
        </ListItem>
        <ListItem disablePadding>
          <Tooltip title={minimized ? "Jobs" : ""} placement="right">
            <ListItemButton
              selected={currentPage === 'jobs'}
              onClick={() => handleListItemClick('jobs')}
              sx={{
                backgroundColor:
                  currentPage === 'jobs' ? 'action.selected' : 'transparent',
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  },
                },
                justifyContent: minimized ? 'center' : 'flex-start',
                minHeight: 48,
                px: minimized ? 1 : 2,
              }}
            >
              <ListItemIcon sx={{ minWidth: minimized ? 0 : 56, justifyContent: 'center' }}>
                <WorkIcon />
              </ListItemIcon>
              {!minimized && <ListItemText primary="Jobs" />}
            </ListItemButton>
          </Tooltip>
        </ListItem>
        <ListItem disablePadding>
          <Tooltip title={minimized ? "Customers" : ""} placement="right">
            <ListItemButton
              selected={currentPage === 'customers'}
              onClick={() => handleListItemClick('customers')}
              sx={{
                backgroundColor:
                  currentPage === 'customers' ? 'action.selected' : 'transparent',
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  },
                },
                justifyContent: minimized ? 'center' : 'flex-start',
                minHeight: 48,
                px: minimized ? 1 : 2,
              }}
            >
              <ListItemIcon sx={{ minWidth: minimized ? 0 : 56, justifyContent: 'center' }}>
                <BusinessIcon />
              </ListItemIcon>
              {!minimized && <ListItemText primary="Customers" />}
            </ListItemButton>
          </Tooltip>
        </ListItem>
 
        <ListItem disablePadding>
          <Tooltip title={minimized ? "Schedule" : ""} placement="right">
            <ListItemButton
              selected={currentPage === 'schedule'}
              onClick={() => handleListItemClick('schedule')}
              sx={{
                backgroundColor:
                  currentPage === 'schedule' ? 'action.selected' : 'transparent',
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  },
                },
                justifyContent: minimized ? 'center' : 'flex-start',
                minHeight: 48,
                px: minimized ? 1 : 2,
              }}
            >
              <ListItemIcon sx={{ minWidth: minimized ? 0 : 56, justifyContent: 'center' }}>
                <AssignmentIcon />
              </ListItemIcon>
              {!minimized && <ListItemText primary="Schedule" />}
            </ListItemButton>
          </Tooltip>
        </ListItem>
          
        <ListItem disablePadding>
          <Tooltip title={minimized ? "Map" : ""} placement="right">
            <ListItemButton
              selected={currentPage === 'map'}
              onClick={() => handleListItemClick('map')}
              sx={{
                backgroundColor:
                  currentPage === 'map' ? 'action.selected' : 'transparent',
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  },
                },
                justifyContent: minimized ? 'center' : 'flex-start',
                minHeight: 48,
                px: minimized ? 1 : 2,
              }}
            >
              <ListItemIcon sx={{ minWidth: minimized ? 0 : 56, justifyContent: 'center' }}>
                <MapIcon />
              </ListItemIcon>
              {!minimized && <ListItemText primary="Map" />}
            </ListItemButton>
          </Tooltip>
        </ListItem>

        <ListItem disablePadding>
          <Tooltip title={minimized ? "Income" : ""} placement="right">
            <ListItemButton
              selected={currentPage === 'income'}
              onClick={() => handleListItemClick('income')}
              sx={{
                backgroundColor:
                  currentPage === 'income' ? 'action.selected' : 'transparent',
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  },
                },
                justifyContent: minimized ? 'center' : 'flex-start',
                minHeight: 48,
                px: minimized ? 1 : 2,
              }}
            >
              <ListItemIcon sx={{ minWidth: minimized ? 0 : 56, justifyContent: 'center' }}>
                <BarChartIcon />
              </ListItemIcon>
              {!minimized && <ListItemText primary="Income" />}
            </ListItemButton>
          </Tooltip>
        </ListItem>

      </List>
      <Divider />
        </>
      )}
      <List>
        {!minimized && (
          <Typography variant="overline" sx={{ px: 2, py: 1, display: 'block' }}>
            Tier Items
          </Typography>
        )}
        <Tooltip title={minimized ? "Scan" : ""} placement="right">
          <ListItemButton
            sx={{ 
              pl: minimized ? 1 : 4,
              justifyContent: minimized ? 'center' : 'flex-start',
              minHeight: 48,
            }}
            selected={currentPage === 'scan'}
            onClick={() => handleListItemClick('scan')}
          >
            <ListItemIcon sx={{ minWidth: minimized ? 0 : 56, justifyContent: 'center' }}>
              <QrCodeScannerIcon />
            </ListItemIcon>
            {!minimized && <ListItemText primary="Scan" />}
          </ListItemButton>
        </Tooltip>
        <Tooltip title={minimized ? "Clock In" : ""} placement="right">
          <ListItemButton
            sx={{ 
              pl: minimized ? 1 : 4,
              justifyContent: minimized ? 'center' : 'flex-start',
              minHeight: 48,
            }}
            selected={currentPage === 'clockin'}
            onClick={() => handleListItemClick('clockin')}
          >
            <ListItemIcon sx={{ minWidth: minimized ? 0 : 56, justifyContent: 'center' }}>
              <AccessTimeIcon />
            </ListItemIcon>
            {!minimized && <ListItemText primary="Clock In" />}
          </ListItemButton>
        </Tooltip>
        {isFieldWorker && (
          <Tooltip title={minimized ? "Find Job" : ""} placement="right">
            <ListItemButton
              sx={{ 
                pl: minimized ? 1 : 4,
                justifyContent: minimized ? 'center' : 'flex-start',
                minHeight: 48,
              }}
              selected={currentPage === 'findjob'}
              onClick={() => handleListItemClick('findjob')}
            >
              <ListItemIcon sx={{ minWidth: minimized ? 0 : 56, justifyContent: 'center' }}>
                <SearchIcon />
              </ListItemIcon>
              {!minimized && <ListItemText primary="Find Job" />}
            </ListItemButton>
          </Tooltip>
        )}
      </List>
      {!isFieldWorker && (
        <>
          <Divider />
          <List>
            {!minimized && (
              <Typography variant="overline" sx={{ px: 2, py: 1, display: 'block' }}>
                Admin items
              </Typography>
            )}
        {isAdmin && (
        <ListItem disablePadding>
          <Tooltip title={minimized ? "Users" : ""} placement="right">
            <ListItemButton
              selected={currentPage === 'users'}
              onClick={() => handleListItemClick('users')}
              sx={{
                backgroundColor:
                  currentPage === 'users' ? 'action.selected' : 'transparent',
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  },
                },
                justifyContent: minimized ? 'center' : 'flex-start',
                minHeight: 48,
                px: minimized ? 1 : 2,
              }}
            >
              <ListItemIcon sx={{ minWidth: minimized ? 0 : 56, justifyContent: 'center' }}>
                <PeopleIcon />
              </ListItemIcon>
              {!minimized && <ListItemText primary="Users" />}
            </ListItemButton>
          </Tooltip>
        </ListItem>
        )}
        <ListItem disablePadding>
          <Tooltip title={minimized ? "Attendance Sheet" : ""} placement="right">
            <ListItemButton
              selected={currentPage === 'attendance'}
              onClick={() => handleListItemClick('attendance')}
              sx={{
                backgroundColor:
                  currentPage === 'attendance' ? 'action.selected' : 'transparent',
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  },
                },
                justifyContent: minimized ? 'center' : 'flex-start',
                minHeight: 48,
                px: minimized ? 1 : 2,
              }}
            >
              <ListItemIcon sx={{ minWidth: minimized ? 0 : 56, justifyContent: 'center' }}>
                <EventNoteIcon />
              </ListItemIcon>
              {!minimized && <ListItemText primary="Attendance Sheet" />}
            </ListItemButton>
          </Tooltip>
        </ListItem>
        <ListItem disablePadding>
          <Tooltip title={minimized ? "Product Types" : ""} placement="right">
            <ListItemButton
              selected={currentPage === 'producttype'}
              onClick={() => handleListItemClick('producttype')}
              sx={{
                backgroundColor:
                  currentPage === 'producttype' ? 'action.selected' : 'transparent',
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  },
                },
                justifyContent: minimized ? 'center' : 'flex-start',
                minHeight: 48,
                px: minimized ? 1 : 2,
              }}
            >
              <ListItemIcon sx={{ minWidth: minimized ? 0 : 56, justifyContent: 'center' }}>
                <CategoryIcon />
              </ListItemIcon>
              {!minimized && <ListItemText primary="Product Types" />}
            </ListItemButton>
          </Tooltip>
        </ListItem>
        
        <ListItem disablePadding>
          <Tooltip title={minimized ? "Product Detail Status" : ""} placement="right">
            <ListItemButton
              selected={currentPage === 'productdetailstatus'}
              onClick={() => handleListItemClick('productdetailstatus')}
              sx={{
                backgroundColor:
                  currentPage === 'productdetailstatus' ? 'action.selected' : 'transparent',
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  },
                },
                justifyContent: minimized ? 'center' : 'flex-start',
                minHeight: 48,
                px: minimized ? 1 : 2,
              }}
            >
              <ListItemIcon sx={{ minWidth: minimized ? 0 : 56, justifyContent: 'center' }}>
                <CheckCircleIcon />
              </ListItemIcon>
              {!minimized && <ListItemText primary="Product Detail Status" />}
            </ListItemButton>
          </Tooltip>
        </ListItem>
        <ListItem disablePadding>
          <Tooltip title={minimized ? "Type of Work" : ""} placement="right">
            <ListItemButton
              selected={currentPage === 'typeofwork'}
              onClick={() => handleListItemClick('typeofwork')}
              sx={{
                backgroundColor:
                  currentPage === 'typeofwork' ? 'action.selected' : 'transparent',
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  },
                },
                justifyContent: minimized ? 'center' : 'flex-start',
                minHeight: 48,
                px: minimized ? 1 : 2,
              }}
            >
              <ListItemIcon sx={{ minWidth: minimized ? 0 : 56, justifyContent: 'center' }}>
                <BuildIcon />
              </ListItemIcon>
              {!minimized && <ListItemText primary="Type of Work" />}
            </ListItemButton>
          </Tooltip>
        </ListItem>
        <ListItem disablePadding>
          <Tooltip title={minimized ? "Integrations" : ""} placement="right">
            <ListItemButton
              selected={currentPage === 'integrations'}
              onClick={() => handleListItemClick('integrations')}
              sx={{
                backgroundColor:
                  currentPage === 'integrations' ? 'action.selected' : 'transparent',
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  },
                },
                justifyContent: minimized ? 'center' : 'flex-start',
                minHeight: 48,
                px: minimized ? 1 : 2,
              }}
            >
              <ListItemIcon sx={{ minWidth: minimized ? 0 : 56, justifyContent: 'center' }}>
                <SettingsIcon />
              </ListItemIcon>
              {!minimized && <ListItemText primary="Integrations" />}
            </ListItemButton>
          </Tooltip>
        </ListItem>
      </List>
        </>
      )}
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { xs: 0, md: drawerWidth }, flexShrink: { md: 0 } }}
      aria-label="navigation"
    >
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            bgcolor: 'background.paper',
          },
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            bgcolor: 'background.paper',
            transition: 'width 0.3s ease',
            overflowX: 'hidden',
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
}
